/**
 * PWA helpers: per-tenant service worker scope, install prompts, push subscription,
 * and honest capability detection (iPhone: push only after "Add to Home Screen").
 */
import { useEffect, useState } from 'react';

export function isStandalone(): boolean {
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

export function isIOS(): boolean {
  const ua = navigator.userAgent;
  return /iPad|iPhone|iPod/.test(ua) || (ua.includes('Mac') && 'ontouchend' in document);
}

export type PushSupport =
  | { kind: 'supported' }
  | { kind: 'ios-needs-install' } // Safari tab on iOS/iPadOS: Web Push requires the installed web app
  | { kind: 'unsupported' }
  | { kind: 'denied' };

export function pushSupport(): PushSupport {
  const hasApi = 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
  if (isIOS() && !isStandalone()) return { kind: 'ios-needs-install' };
  if (!hasApi) return { kind: 'unsupported' };
  if (Notification.permission === 'denied') return { kind: 'denied' };
  return { kind: 'supported' };
}

/** Registers the shared worker under this tenant's scope only (production builds). */
export async function registerTenantWorker(slug: string): Promise<ServiceWorkerRegistration | null> {
  if (!import.meta.env.PROD || !('serviceWorker' in navigator)) return null;
  try {
    return await navigator.serviceWorker.register(`/s/${slug}/sw.js`, { scope: `/s/${slug}/`, updateViaCache: 'none' });
  } catch (e) {
    console.warn('service worker registration failed', e);
    return null;
  }
}

function urlB64ToUint8Array(base64: string): Uint8Array<ArrayBuffer> {
  const pad = '='.repeat((4 - (base64.length % 4)) % 4);
  const raw = atob((base64 + pad).replace(/-/g, '+').replace(/_/g, '/'));
  const out = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

/** Asks permission (must be called from a user gesture) and returns the subscription JSON. */
export async function subscribePush(slug: string, vapidKey: string): Promise<PushSubscriptionJSON> {
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') throw new Error(permission === 'denied' ? 'denied' : 'dismissed');
  const reg = (await navigator.serviceWorker.getRegistration(`/s/${slug}/`)) ?? (await registerTenantWorker(slug));
  if (!reg) throw new Error('no-worker');
  await navigator.serviceWorker.ready;
  const existing = await reg.pushManager.getSubscription();
  const sub = existing ?? (await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlB64ToUint8Array(vapidKey) }));
  return sub.toJSON();
}

export async function currentPushEndpoint(slug: string): Promise<string | null> {
  try {
    const reg = await navigator.serviceWorker?.getRegistration(`/s/${slug}/`);
    return (await reg?.pushManager.getSubscription())?.endpoint ?? null;
  } catch {
    return null;
  }
}

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let deferredPrompt: BeforeInstallPromptEvent | null = null;
const listeners = new Set<() => void>();
if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferredPrompt = e as BeforeInstallPromptEvent;
    listeners.forEach((l) => l());
  });
  window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
    listeners.forEach((l) => l());
  });
}

export function useInstallPrompt() {
  const [, force] = useState(0);
  useEffect(() => {
    const l = () => force((n) => n + 1);
    listeners.add(l);
    return () => void listeners.delete(l);
  }, []);
  return {
    canPrompt: Boolean(deferredPrompt),
    isInstalled: isStandalone(),
    needsIOSInstructions: isIOS() && !isStandalone(),
    prompt: async () => {
      if (!deferredPrompt) return false;
      await deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      deferredPrompt = null;
      return outcome === 'accepted';
    },
  };
}

/** Owner pages install as their own app: swap the manifest link while inside /owner. */
export function useManifest(slug: string, area: 'client' | 'owner') {
  useEffect(() => {
    const link = document.querySelector<HTMLLinkElement>('link[rel="manifest"]');
    if (!link) return;
    const v = new URL(link.href, location.href).searchParams.get('v');
    link.href = `/s/${slug}/${area === 'owner' ? 'owner' : 'manifest'}.webmanifest${v ? `?v=${v}` : ''}`;
  }, [slug, area]);
}
